import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { HeroCollage } from "@/components/HeroCollage";

const EASE = [0.22, 1, 0.36, 1];
const lines = [
  { text: "Less to", italic: false },
  { text: "remember.", italic: false },
  { text: "More peace", italic: true },
  { text: "of mind.", italic: true },
];

const MaskedLine = ({ text, italic, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${italic ? "italic text-[#3D5585]" : ""}`}
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.3 + i * 0.12, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

export const HeroSection = () => (
  <section className="grain relative overflow-hidden pb-20 pt-32 sm:pt-40 lg:pb-28" data-testid="hero-section">
    <div className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-[#E3E8F1] blur-3xl" />
    <div className="pointer-events-none absolute -left-32 bottom-0 h-[360px] w-[360px] rounded-full bg-[#F2ECE1] blur-3xl" />
    <div className="relative mx-auto grid max-w-6xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12">
      <div className="lg:col-span-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          data-testid="in-development-badge"
          className="inline-flex items-center gap-2 rounded-full border border-[#1C2B4A]/20 bg-[#E3E8F1] px-3.5 py-1.5 text-xs font-semibold tracking-wide text-[#1C2B4A]"
        >
          <span className="relative flex h-2 w-2">
            <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-[#1C2B4A]" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#1C2B4A]" />
          </span>
          In development
        </motion.div>
        <h1
          data-testid="hero-headline"
          className="font-display mt-8 text-[3.3rem] font-normal leading-[0.98] tracking-[-0.03em] text-[#16203A] sm:text-7xl lg:text-[5.6rem]"
        >
          {lines.map((l, i) => <MaskedLine key={l.text} {...l} i={i} />)}
        </h1>
        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
          className="mt-8 max-w-xl text-lg leading-relaxed text-[#464E62]"
        >
          Otti is being built to turn everyday emails, documents and voice notes into shared tasks, events and reminders — so you can see what needs doing, who’s handling it and what still needs attention.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4"
        >
          <button
            data-testid="hero-scroll-waitlist"
            onClick={() => scrollToId("waitlist")}
            className="group inline-flex items-center gap-3 rounded-full bg-[#1C2B4A] py-3 pl-6 pr-3 text-base font-semibold text-[#FAF7F2] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#121C33]"
          >
            Join the waitlist
            <span className="grid h-8 w-8 place-items-center rounded-full bg-white/15 transition-transform duration-300 group-hover:translate-y-0.5">
              <ArrowDown className="h-4 w-4" />
            </span>
          </button>
        </motion.div>
      </div>
      <div className="lg:col-span-6">
        <HeroCollage />
      </div>
    </div>
  </section>
);
